import React, { useState, useEffect } from "react";

import { Trans, useTranslation } from "react-i18next";
import { isEmpty } from "lodash";
import CreatableSelect from "react-select/creatable";

import LookupsAPI from "../../API/lookups.api";
import InstructionNotification from "../Notifications/InstructionNotification";
import InformationTooltip from "../Tooltips/InformationTooltip";

const BiasCorrectionForm = ({
    modelId,
    isVisible,
    addedCorrections,
    setAddedCorrections,
    referenceCorrections,
}) => {
    //Form Data
    const [currentVals, setCurrentVals] = useState({});
    const [scannerOptions, setScannerOptions] = useState([]);
    const [scannerOptionsLoading, setScannerOptionsLoading] = useState(false);
    const [error, setError] = useState({});

    const [addAttempted, setAddAttempted] = useState(false);

    const { t } = useTranslation();

    const headers = [t("deviceId"), t("slope"), t("bias"), t("remove")];

    useEffect(() => {
        if (isVisible && modelId) {
            resetForm(referenceCorrections);
        }
    }, [modelId]);

    useEffect(() => {
        if (referenceCorrections && scannerOptions)
            filterScanners(scannerOptions, referenceCorrections);
    }, [referenceCorrections]);

    useEffect(() => {
        if (currentVals?.slope <= 0) {
            setError({ slope: "Must be bigger than 0" });
        } else {
            setError({});
        }
    }, [currentVals]);

    const getScanners = async (corrections) => {
        if (corrections) {
            setScannerOptionsLoading(true);
            const result = await LookupsAPI.getScanners();
            //Filter already existing scannerIds
            filterScanners(result, corrections);
        }
    };

    const filterScanners = (scanners, corrections) => {
        const mappedResult = scanners?.filter(
            (scanner) => !corrections[scanner.value]
        );
        setScannerOptions(mappedResult);
        setScannerOptionsLoading(false);
    };

    const resetForm = (corrections) => {
        setAddAttempted(false);
        getScanners(corrections);
        setCurrentVals((prevVals) => {
            const emptyVals = {};
            Object.keys(prevVals).forEach((key) => {
                emptyVals[key] = "";
            });
            return emptyVals;
        });
    };

    const handleInputChange = (e) => {
        setCurrentVals((prevVals) => {
            return { ...prevVals, [e.target.name]: e.target.value };
        });
    };

    const handleScannerSelectChange = (value, action) => {
        setCurrentVals((prevVals) => {
            const isCreated = action.action === "create-option";
            return {
                ...prevVals,
                ...value,
                isCreated,
            };
        });
    };

    const isAddDisabled = () => {
        //Check if there's a field that's empty to disable the add button
        return !(currentVals.bias && currentVals.slope && currentVals.value);
    };

    const handleRemoveCorrection = (index) => {
        //Remove the removed correction from the added corrections
        setAddedCorrections((prevCorrections) => {
            const currentCorrection = prevCorrections[index];
            let newCorrections = [...prevCorrections];
            if (referenceCorrections[currentCorrection.value]) {
                newCorrections[index] = {
                    ...newCorrections[index],
                    isDeleted: true,
                };
            } else {
                newCorrections.splice(index, 1);
            }
            return newCorrections;
        });
        //Return the removed correction to the scannerId options
        setScannerOptions((prevOptions) => {
            const selectedCorrection = addedCorrections[index];
            return [
                ...prevOptions,
                {
                    label: selectedCorrection.label,
                    value: selectedCorrection.value,
                },
            ];
        });
    };

    const addCorrection = () => {
        setAddAttempted(true);
        if (isEmpty(error)) {
            //Add the selected scannerId(s) to the added corrections
            setAddedCorrections((prevAdded) => {
                //Remove the selected scannerId from the scannerId options
                setScannerOptions((prevOptions) => {
                    const filtered = prevOptions.filter(
                        (option) => currentVals.value !== option.value
                    );

                    return filtered;
                });
                return [...prevAdded, currentVals];
            });
            resetForm();
        }
    };

    const getVisibleCorrections = () => {
        return addedCorrections.filter((correction) => !correction.isDeleted);
    };

    const showValidationError = (optionName) => {
        return error[optionName] && addAttempted ? (
            <div className="invalid-input-message flex items-center">
                <i className="fas fa-exclamation-triangle"></i>
                <span className="invalid-input-message-text">
                    {error[optionName]}
                </span>
            </div>
        ) : (
            <></>
        );
    };

    const fields = [
        {
            isRequired: true,
            infoTooltip: t("defaultScannerTooltip"),
            label: <Trans i18nKey="deviceId">Device ID</Trans>,
            input: (
                <CreatableSelect
                    options={scannerOptions}
                    isClearable={false}
                    isDisabled={scannerOptionsLoading}
                    isLoading={scannerOptionsLoading}
                    value={
                        currentVals?.value
                            ? {
                                  value: currentVals.value,
                                  label: currentVals.label,
                              }
                            : ""
                    }
                    onChange={handleScannerSelectChange}
                    className="z-13"
                    placeholder={t("selectOrAddPlaceholder", {
                        entity: t("deviceId"),
                    })}
                    menuPosition="fixed"
                    menuPlacement="auto"
                    menuShouldScrollIntoView={false}
                />
            ),
        },
        {
            isRequired: true,
            isMulti: true,
            label: [
                <Trans i18nKey="slope" key="slope-label">
                    Slope
                </Trans>,
                <Trans i18nKey="bias" key="bias-label">
                    Bias
                </Trans>,
            ],
            input: [
                <>
                    <input
                        key="slope"
                        type="number"
                        className="input-element stacked-input-element"
                        placeholder="1.00"
                        onChange={handleInputChange}
                        name="slope"
                        min={1}
                        value={currentVals?.slope || ""}
                    />
                    {showValidationError("slope")}
                </>,
                <input
                    key="bias"
                    type="number"
                    className="input-element"
                    placeholder="0.00"
                    onChange={handleInputChange}
                    name="bias"
                    value={currentVals?.bias || ""}
                />,
            ],
        },
    ];

    const renderFields = () => {
        return (
            fields &&
            fields.map((inputField, index) => {
                if (inputField?.isMulti) {
                    return (
                        <div
                            className="flex w-full justify-between"
                            key={index}
                        >
                            {inputField &&
                                inputField.label?.map((input, innerIndex) => {
                                    return (
                                        <div
                                            className="tags input-group w-full"
                                            key={`${index}-${innerIndex}`}
                                        >
                                            <label
                                                className={`uppercase text-xs ${
                                                    inputField?.isRequired &&
                                                    "required"
                                                }`}
                                            >
                                                {input}
                                            </label>
                                            {inputField?.input?.[innerIndex]}
                                        </div>
                                    );
                                })}
                        </div>
                    );
                } else {
                    return (
                        <div className="input-group" key={index}>
                            <div className="flex">
                                <label
                                    className={`uppercase text-xs ${
                                        inputField?.isRequired && "required"
                                    }`}
                                >
                                    {inputField?.label}
                                </label>
                                {inputField.infoTooltip && (
                                    <div className="text-xs ml-1">
                                        <InformationTooltip
                                            message={inputField.infoTooltip}
                                        />
                                    </div>
                                )}
                            </div>
                            {inputField?.input}
                        </div>
                    );
                }
            })
        );
    };

    const renderHeader = () => {
        return (
            headers &&
            headers.map((header, index) => {
                return (
                    <td key={index}>
                        <div className="font-bold px-6 py-3 text-center text-xs text-gray-500 uppercase tracking-wider">
                            <div className="flex items-center justify-center">
                                <span className="ml-2">{header}</span>
                            </div>
                        </div>
                    </td>
                );
            })
        );
    };

    const renderCorrections = () => {
        return (
            addedCorrections &&
            addedCorrections.map((correction, index) => {
                if (!correction.isDeleted) {
                    return (
                        <tr key={index}>
                            <td>
                                <div className="px-6 py-3 whitespace-wrap">
                                    {correction?.label}
                                </div>
                            </td>
                            <td>
                                <div className="px-6 py-3 whitespace-wrap">
                                    {correction?.slope}
                                </div>
                            </td>
                            <td>
                                <div className="px-6 py-3 whitespace-wrap">
                                    {correction?.bias}
                                </div>
                            </td>
                            <td>
                                <div className="px-6 py-3 whitespace-wrap">
                                    <button
                                        onClick={() =>
                                            handleRemoveCorrection(index)
                                        }
                                        className="table-btn table-btn-orange m-0"
                                    >
                                        <i className="fas fa-trash-alt" />
                                    </button>
                                </div>
                            </td>
                        </tr>
                    );
                }
            })
        );
    };

    const renderChildren = () => {
        return (
            <>
                <div className="main-customer-form-inputs">
                    {renderFields()}
                </div>
                <div className="steps-manage flex justify-end">
                    <button
                        className={`secondary-green-btn mng-model modal-btn-open mb-3 ${
                            isAddDisabled() ? "disabled" : ""
                        }`}
                        disabled={isAddDisabled()}
                        onClick={addCorrection}
                    >
                        <Trans i18nKey="add">Add</Trans>
                    </button>
                </div>
                <div className="input-group">
                    <label htmlFor="scan-input-1" className="uppercase">
                        <Trans i18nKey="biasCorrection">Bias Correction</Trans>
                        <span className="text-primary-orange ml-1">
                            ({getVisibleCorrections()?.length})
                        </span>
                    </label>
                </div>
                <div className="table-responsive shadow overflow-hidden border-b border-gray-200 sm:rounded-lg h-small-with-scroll small-scroll-x small-scroll-y">
                    <table className="table-striped min-w-full divide-y divide-gray-200 text-xs text-center w-full">
                        <thead className="parent-head">
                            <tr>{renderHeader()}</tr>
                        </thead>

                        <tbody>
                            {isEmpty(getVisibleCorrections()) ? (
                                <InstructionNotification
                                    colSpan={headers?.length}
                                    message={t("noBiasCorrection")}
                                />
                            ) : (
                                renderCorrections()
                            )}
                        </tbody>
                    </table>
                </div>
            </>
        );
    };

    return renderChildren();
};

export default BiasCorrectionForm;
